import React, {
  Suspense,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
// @ import dependencies
import { useDispatch, useSelector } from "react-redux";
import { Route, Routes, useLocation, useNavigate } from "react-router-dom";
// @import pages
import NotFound from "pages/notFound";
// @import components
import Sidebar from "./Sidebar";
import Header from "./Header";
import { PageLoader } from "components/loader";
import Alerts from "components/alert";
import LimitReachedModal from "components/modal/LimitReachedModal";
import SVG from "components/renderSvg";
import euro from "media/svgs/gbp.svg";
//@import redux slices

//@import routes
import { AppRoutes } from "routes/AppRoutes";

import useDocument from "hooks/useDocument";
import {
  storeVenderData,
  setStripeOnboardingRequired,
  setStripeStatus,
} from "store/slices/venderslice";
import useCurrentUser from "hooks/useCurrentUser";
import useGlobalNotifications from "hooks/useGlobalNotifications";
import { TrendReportProvider } from "context/TrendReportContext";
import { initiateStripeOnboarding, getStripeOnboardStatus } from "services/vendorAPI";
import toast from "react-hot-toast";
import { Crisp } from "crisp-sdk-web";

import ProductTour from 'components/ProductTour';

const REQUIREMENT_LABELS = {
  "individual.verification.document": "Identity Verification Document",
  "individual.id_number": "National ID Number",
  "business_profile.url": "Business Website URL",
  external_account: "Bank Account details",
  "relationship.representative": "Representative Information",
  "representative.verification.document": "Representative Identity Document",
  "company.tax_id": "Company Tax ID",
  "individual.dob.day": "Date of Birth (Day)",
  "individual.dob.month": "Date of Birth (Month)",
  "individual.dob.year": "Date of Birth (Year)",
  "individual.first_name": "First Name",
  "individual.last_name": "Last Name",
  "individual.address.line1": "Address Line 1",
  "individual.address.city": "Address City",
  "individual.address.postal_code": "Address Postal Code",
  "individual.email": "Email Address",
  "individual.phone": "Phone Number",
};

const getRequirementLabel = (req) => {
  if (REQUIREMENT_LABELS[req]) return REQUIREMENT_LABELS[req];
  return req.replace(/[._]/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
};

const GlobalRealtimeListener = () => {
  useGlobalNotifications("vendor");
  return null;
};

const Layout = () => {
  const { isToggle } = useSelector((state) => state.commonState);

  const { venderData } = useSelector((state) => state.vender);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation(); // Get current route
  const { user, loading: userLoading } = useCurrentUser();
  const { fetchUserById } = useDocument("merchants");
  const [loading, setIsloading] = useState(true);

  const stripeOnboardingRequired = useSelector(
    (state) => state.vender.stripeOnboardingRequired,
  );
  const stripeStatus = useSelector((state) => state.vender.stripeStatus);
  const [stripeLoading, setStripeLoading] = useState(false);

  // Profile completion percentage – same calculation as Sidebar
  const profileStatus = useMemo(() => {
    if (!venderData) return 0;
    let status = 0;
    if (venderData?.addBatch) status += 20;
    if (venderData?.addHall) status += 20;
    if (venderData?.addTrainer) status += 20;
    if (venderData?.profileCompleted) status += 20;
    if (venderData?.serviceCompleted) status += 20;
    return Math.min(status, 100);
  }, [venderData]);

  const bannerContent = useMemo(() => {
    if (!stripeStatus) {
      return {
        text: "Stripe configuration required to accept bookings.",
        buttonText: "Set Up Payouts",
      };
    }

    const { details_submitted, requirements } = stripeStatus;

    if (details_submitted) {
      const dueList = requirements?.currently_due || [];
      if (dueList.length > 0) {
        const readableReqs = dueList.map(getRequirementLabel).join(", ");
        return {
          text: `Stripe verification pending. Action required: ${readableReqs}.`,
          buttonText: "Complete Verification",
        };
      }
      return {
        text: "Stripe verification pending. Payouts will enable once Stripe approves your details.",
        buttonText: "Check Stripe Account",
      };
    }

    return {
      text: "Stripe configuration required to accept bookings.",
      buttonText: "Set Up Payouts",
    };
  }, [stripeStatus]);

  useEffect(() => {
    const checkStripeStatus = async () => {
      if (!user) return;
      try {
        const res = await getStripeOnboardStatus();
        if (res.success) {
          dispatch(setStripeStatus(res.data));
          dispatch(setStripeOnboardingRequired(!res.data.is_fully_onboarded));
        } else {
          dispatch(setStripeStatus(null));
          dispatch(setStripeOnboardingRequired(true));
        }
      } catch (err) {
        console.error("Error fetching Stripe status in layout:", err);
        dispatch(setStripeStatus(null));
        dispatch(setStripeOnboardingRequired(true));
      }
    };
    checkStripeStatus();

    // Show Crisp and Initialize Userjot for authenticated users
    if (user) {
      Crisp.chat.show();
      if (!window.uj) {
        window.$ujq = window.$ujq || [];
        window.uj = window.uj || new Proxy({}, { get: (_, p) => (...a) => window.$ujq.push([p, ...a]) });
        const script = document.createElement('script');
        script.src = 'https://cdn.userjot.com/sdk/v2/uj.js';
        script.type = 'module';
        script.async = true;
        document.head.appendChild(script);
        window.uj.init('cmm22ga0r16to0iphw761pzok', {
          widget: true,
          position: 'left',
          theme: 'auto'
        });
      }
    }

    return () => {
      Crisp.chat.hide();
    };
  }, [user, dispatch]);

  const handleSetupPayouts = async () => {
    setStripeLoading(true);
    try {
      const res = await initiateStripeOnboarding();
      if (res.success && res.data?.onboarding_url) {
        window.location.href = res.data.onboarding_url;
      } else {
        toast.error(res.error || "Failed to initiate Stripe onboarding.");
      }
    } catch (err) {
      toast.error("An error occurred. Please try again.");
    } finally {
      setStripeLoading(false);
    }
  };

  const handleLogout = useCallback(() => {
    navigate("/login");
    // Clear auth tokens; other localStorage keys (trial_end, etc.) can remain
    localStorage.clear();
    dispatch({ type: "RESET_STATE" });
    Crisp.session.reset();
  }, [navigate, dispatch]);

  useEffect(() => {
    if (!userLoading) {
      if (user) {
        setIsloading(false);
      } else {
        setIsloading(false);
        handleLogout();
      }
    }
  }, [user, userLoading, handleLogout]);

  // Session validity is now managed by the access-token / refresh-token lifecycle
  // in vendorAPI.js (silent refresh interceptor). No manual timestamp check needed.

  // Gating by subscription/trial
  useEffect(() => {
    const verifySubscription = async () => {
      if (!user) return;

      // If we just returned from Stripe, verify the checkout session
      const params = new URLSearchParams(window.location.search);
      const sessionId = params.get("session_id");
      if (sessionId) {
        // TODO: Verify checkout session via backend API

        // This would call: POST /api/v1/vendor/stripe/verify-checkout-session
        console.log("Checkout session ID:", sessionId);
      }

      const res = await fetchUserById(user.uid || user.id);
      // Handle API response structure - could be direct object or { data: [...] }
      let merchant = res?.data || res;

      // Check if merchant is an object with a data property (nested structure: { data: [...] })
      if (
        merchant &&
        typeof merchant === "object" &&
        !Array.isArray(merchant) &&
        merchant.data &&
        !merchant.id
      ) {
        merchant = Array.isArray(merchant.data)
          ? merchant.data[0]
          : merchant.data;
      }
      // Check if merchant is an array
      else if (Array.isArray(merchant)) {
        merchant = merchant[0];
      }
      // Also handle if res.data.data exists (nested data from API wrapper)
      else if (res?.data?.data && !merchant?.id) {
        merchant = Array.isArray(res.data.data)
          ? res.data.data[0]
          : res.data.data;
      }

      // Validate merchant object - should have at least id or subscription_tier
      if (!merchant || typeof merchant !== "object" || Array.isArray(merchant))
        return;
      if (!merchant.id && !merchant.subscription_tier && !merchant.email)
        return;

      // Update Redux vendor data so UI like header shows current subscription badge
      dispatch(storeVenderData(merchant));

      // Sync trial_end to localStorage for persistent banner logic across pages
      if (merchant.trial_ends_at || merchant.trial_end) {
        localStorage.setItem(
          "trial_end",
          merchant.trial_ends_at || merchant.trial_end,
        );
      }

      const now = Date.now();

      // Handle trial_ends_at - check if it's a valid date (not the default "0001-01-01" date)
      // Normalize trial date check with fallback for trial_end and trialEndsAtMillis
      let trialEnds = 0;
      const trialEndsSource =
        merchant.trial_ends_at ||
        merchant.trial_end ||
        merchant.trialEndsAtMillis;

      if (trialEndsSource) {
        if (typeof trialEndsSource === "number") {
          trialEnds = trialEndsSource;
        } else {
          const trialDate = new Date(trialEndsSource).getTime();
          // Check if date is valid and not the default zero date (before 1970)
          if (trialDate > 0 && trialDate > new Date("1970-01-01").getTime()) {
            trialEnds = trialDate;
          }
        }
      }

      // Fallback to localStorage if trial date is missing in profile (already synced above, but kept for robust logic)
      if (trialEnds === 0) {
        const localTrialEnd = localStorage.getItem("trial_end");
        if (localTrialEnd) {
          const trialDate = new Date(localTrialEnd).getTime();
          if (trialDate > now) {
            trialEnds = trialDate;
          }
        }
      }
    };
    if (user) {
      verifySubscription();
    }
  }, [user, location.pathname, navigate, dispatch, fetchUserById]);

  const trialEndDate = localStorage.getItem("trial_end");
  const daysRemaining = useMemo(() => {
    if (!trialEndDate) return 0;
    const now = new Date();
    const end = new Date(trialEndDate);
    const diff = end - now;
    return Math.max(0, Math.ceil(diff / (1000 * 60 * 60 * 24)));
  }, [trialEndDate]);

  const isTrialActive = useMemo(() => {
    if (!trialEndDate) return false;
    return new Date(trialEndDate) > new Date();
  }, [trialEndDate]);

  if (loading) return <PageLoader />;

  return (
    <TrendReportProvider>
      <GlobalRealtimeListener />
      <ProductTour />
      <div className="relative h-screen w-full bg-surface-50 overflow-hidden">
        <Sidebar handleLogout={handleLogout} />
        <Header handleLogout={handleLogout} />
        <LimitReachedModal />
        <div
          id="main-scroll-container"
          className={`h-full overflow-y-auto pt-[64px] pb-6 pr-4 transition-all duration-300 ease-in-out scrollbar-thin scrollbar-thumb-gray-300 ${isToggle ? "ml-0 md:ml-[80px]" : "ml-0 md:ml-[280px]"
            }`}
        >
          {!isTrialActive &&
            (venderData?.subscription_tier === "free" ||
              !venderData?.subscription_tier) && (
              <div className="bg-white border-b border-gray-100 px-6 py-2 flex items-center justify-center gap-4 sticky top-0 z-40 animate-in fade-in duration-500 shadow-sm backdrop-blur-md">
                <div className="flex items-center gap-2">
                  <span className="flex h-1.5 w-1.5 rounded-full bg-gray-300"></span>
                  <p className="text-[10px] font-black text-gray-400 uppercase tracking-[0.2em]">
                    Free Account
                  </p>
                </div>
                <span className="h-4 w-px bg-gray-100"></span>
                <p className="text-[11px] font-medium text-gray-500 italic">
                  Your existing services are active, but you cannot add new ones
                  without upgrading.
                </p>
                <button
                  onClick={() => navigate("/plans")}
                  className="text-[10px] font-bold text-black underline underline-offset-4 hover:text-brand-600 transition-colors ml-2"
                >
                  Upgrade
                </button>
              </div>
            )}
          {stripeOnboardingRequired && profileStatus === 100 && (
            <div className=" border-b border-200 px-4 py-2.5 flex flex-col sm:flex-row items-center justify-center gap-2 sm:gap-4  top-0 z-40 animate-in fade-in duration-500 shadow-sm">
              <div className="flex items-center gap-2">
                <span className="flex h-2 w-2 rounded-full bg-red-500 animate-pulse"></span>
                <p className="text-[10px] font-black text-red-800 uppercase tracking-[0.2em]">
                  Action Required
                </p>
              </div>
              <span className="hidden sm:block h-4 w-px bg-red-200"></span>
              <p className="text-[11px] font-semibold text-gray-900 leading-none text-center sm:text-left">
                {bannerContent.text}
              </p>
              <button
                onClick={handleSetupPayouts}
                disabled={stripeLoading}
                className="text-[10px] font-bold bg-gray-900 text-white px-3.5 py-1.5 rounded-lg shadow-sm hover:bg-gray-700 transition-colors sm:ml-2 flex items-center gap-1.5 disabled:opacity-75 w-full sm:w-auto justify-center"
              >
                {stripeLoading ? (
                  <span className="animate-spin border-2 border-white border-t-transparent rounded-full h-3 w-3"></span>
                ) : null}
                {bannerContent.buttonText}
              </button>
            </div>
          )}
          <div className="mx-auto max-w-[1600px] pt-3 px-2">
            <Alerts />
            <Suspense fallback={<PageLoader />}>
              <Routes>
                {AppRoutes?.map((route, k) => (
                  <Route
                    key={k}
                    path={route.path}
                    element={<route.component />}
                  />
                ))}
                <Route path="*" element={<NotFound />} />
              </Routes>
            </Suspense>
          </div>
        </div>
      </div>
    </TrendReportProvider>
  );
};

export default Layout;
